// SABLON-KEZELŐ — a cikk-vázak és a beszúrható részletek karbantartása.
//
// Szerkesztőségi eszköz, nem üzemeltetési: a tartalom KÉSZÍTÉSÉRŐL szól, ezért saját menüpont, nem
// az Üzemeltetés alá bújtatva. A jogot (`template.manage`) a BFF kényszeríti ki; a menü-szűrés és
// az itteni kapu kényelmi funkció.
//
// A sablon TÖRZSE ugyanaz a formátum, mint a cikké (markdown + HTML-szigetek) — ezért a szerkesztés
// egy sima markdown-mező ÉLŐ ELŐNÉZETTEL, ugyanazzal a `Markdown` komponenssel, ami a cikket is
// rendereli. Így a szerző pontosan azt látja, amit a sablonból kapni fog, és nem kellett új
// szerkesztő-felületet építeni.
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type { Template, TemplateKind } from '@morgens-hub/contracts';
import { PERMISSIONS } from '@morgens-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AppShell } from '@/components/shared/app-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTemplate, useTemplates, useUpdateTemplate } from '@/features/editor/api';
import { Markdown } from '@/features/knowledge/components/markdown';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useErrorText } from '@/lib/use-error-text';
import { usePermissionState } from '@/lib/use-permission';

export const Route = createFileRoute('/sablonok')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: TemplatesPage,
});

/** Az űrlap állapota — új sablonnál `id: null`, meglévőnél a szerkesztett sablon. */
interface FormState {
  id: string | null;
  version: number;
  kind: TemplateKind;
  name: string;
  description: string;
  body: string;
  position: number;
}

const EMPTY_FORM: FormState = {
  id: null,
  version: 0,
  kind: 'snippet',
  name: '',
  description: '',
  body: '',
  position: 0,
};

function TemplatesPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const session = api.session.useQuery(['session']);
  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const permission = usePermissionState(PERMISSIONS.templateManage);
  const templates = useTemplates(undefined, { enabled: permission.allowed });
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const [form, setForm] = useState<FormState | null>(null);

  if (!permission.allowed) {
    return (
      <AppShell session={sessionBody}>
        <h1 className="text-read-xl font-semibold">{t('templates.title')}</h1>
        <p className="text-muted-foreground text-read-sm mt-4">{t('noAccess.body')}</p>
      </AppShell>
    );
  }

  // A ts-rest kliens a nem-2xx választ DOBJA (a query `isError`-ra vált), tehát a `data` itt már
  // csak a 200-as ág — a státusz újra-ellenőrzése holt kód lenne (a lint is jelzi).
  const items = templates.data?.body.templates ?? [];

  const save = (): void => {
    if (!form) return;
    if (!form.name.trim()) {
      toast.error(t('templates.nameRequired'));
      return;
    }
    const payload = {
      kind: form.kind,
      name: form.name.trim(),
      description: form.description.trim(),
      body: form.body,
      position: form.position,
    };
    const done = (): void => {
      toast.success(t('templates.saved'));
      setForm(null);
      void templates.refetch();
    };
    const fail = (error: unknown): void => {
      toast.error(errorText(error));
    };

    if (form.id === null) {
      create.mutate({ body: payload }, { onSuccess: done, onError: fail });
      return;
    }
    update.mutate(
      { params: { id: form.id }, body: { ...payload, version: form.version } },
      { onSuccess: done, onError: fail },
    );
  };

  const archive = (template: Template): void => {
    // A `window.confirm` a repó bevett mintája a visszafordítható, egy lépéses műveleteknél
    // (lásd a szekció/könyv archiválását) — a kivezetés nem törlés, a története megmarad.
    if (!window.confirm(t('templates.archiveConfirm'))) return;
    update.mutate(
      { params: { id: template.id }, body: { archived: true, version: template.version } },
      {
        onSuccess: () => {
          toast.success(t('templates.archived'));
          void templates.refetch();
        },
        onError: (error) => {
          toast.error(errorText(error));
        },
      },
    );
  };

  // Az űrlap MINDKÉT nézetben ugyanaz (új sablon / meglévő módosítása) — csak a keret más.
  const templateForm = form ? (
    <div className="border-border bg-card mt-6 rounded-lg border p-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <Label htmlFor="template-name">{t('templates.fieldName')}</Label>
          <Input
            id="template-name"
            value={form.name}
            onChange={(event) => {
              setForm({ ...form, name: event.target.value });
            }}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="template-kind">{t('templates.fieldKind')}</Label>
          <select
            id="template-kind"
            value={form.kind}
            onChange={(event) => {
              setForm({ ...form, kind: event.target.value as TemplateKind });
            }}
            className="border-border bg-background mt-1.5 h-10 w-full rounded-lg border px-2.5"
          >
            <option value="snippet">{t('templates.kindSnippet')}</option>
            <option value="article">{t('templates.kindArticle')}</option>
          </select>
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="template-description">{t('templates.fieldDescription')}</Label>
        <Input
          id="template-description"
          value={form.description}
          onChange={(event) => {
            setForm({ ...form, description: event.target.value });
          }}
          className="mt-1.5"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div>
          <Label htmlFor="template-body">{t('templates.fieldBody')}</Label>
          <Textarea
            id="template-body"
            value={form.body}
            onChange={(event) => {
              setForm({ ...form, body: event.target.value });
            }}
            className="mt-1.5 min-h-[16rem] font-mono text-sm leading-relaxed"
          />
        </div>
        <div>
          <span className="text-md font-medium">{t('editor.preview')}</span>
      <div className="border-border mt-1.5 min-h-[16rem] rounded-lg border p-4">
            {form.body.trim() ? (
              <Markdown source={form.body} />
            ) : (
              <p className="text-muted-foreground text-sm">{t('editor.previewEmpty')}</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setForm(null);
          }}
        >
          {t('templates.cancel')}
        </Button>
        <Button type="button" onClick={save} disabled={create.isPending || update.isPending}>
          {t('templates.save')}
        </Button>
      </div>
    </div>
  ) : null;

  /*
    SZERKESZTÉS = KÜLÖN NÉZET, nem a lista alá tett panel (a projekt-tulajdonos jelzése, 2026-08-30).
    A panel eddig a lista ALATT nyílt: a szerkesztőnek végig kellett görgetnie a teljes listán, hogy
    egyáltalán MEGLÁSSA, amit szerkeszt — és közben a többi sablon is a képernyőn maradt, ami azt
    sugallta, hogy még mindig a listában van. Ha egy sablont megnyitok, annak a sablonnak a
    szerkesztésébe akarok belépni.

    A lista és az űrlap ezért egymást KIZÁRJA. Ugyanaz az elv, mint a cikk-szerkesztőnél: ott is
    külön képernyő a szerkesztés, kivezető úttal (lásd a navigáció-átalakítás 4. szakaszát).
  */
  if (form) {
    return (
      <AppShell session={sessionBody}>
        <button
          type="button"
          onClick={() => {
            setForm(null);
          }}
          className="text-muted-foreground hover:text-foreground text-md inline-flex items-center gap-1.5"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          {t('templates.backToList')}
        </button>
        <h1 className="text-read-xl mt-3 font-semibold">
          {form.id === null ? t('templates.create') : t('templates.editTitle', { name: form.name })}
        </h1>
        {templateForm}
      </AppShell>
    );
  }

  return (
    <AppShell session={sessionBody}>
      <h1 className="text-read-xl font-semibold">{t('templates.title')}</h1>
      <p className="text-muted-foreground text-read-sm mt-2 max-w-prose">{t('templates.subtitle')}</p>

      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          onClick={() => {
            setForm(EMPTY_FORM);
          }}
        >
          {t('templates.create')}
        </Button>
      </div>

      {templates.isPending && <Skeleton className="mt-4 h-32 w-full" />}
      {/* A HIBA és az ÜRESSÉG külön állapot: az egyikre várni kell, a másikra sablont felvenni. */}
      {templates.isError && (
        <p className="text-muted-foreground text-read-sm mt-4">{t('templates.loadError')}</p>
      )}
      {!templates.isPending && !templates.isError && items.length === 0 && (
        <p className="text-muted-foreground text-read-sm mt-4">{t('templates.empty')}</p>
      )}

      <ul className="mt-4 space-y-2">
        {items.map((template) => (
          <li
            key={template.id}
            className="border-border bg-card flex items-center justify-between gap-4 rounded-lg border p-3"
          >
            <div className="min-w-0">
              <p className="font-medium">{template.name}</p>
              <p className="text-subtle text-md truncate">
                {t(template.kind === 'article' ? 'templates.kindArticle' : 'templates.kindSnippet')}
                {template.description ? ` · ${template.description}` : ''}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setForm({ ...template, id: template.id });
                }}
              >
                {t('templates.edit')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  archive(template);
                }}
              >
                {t('templates.archive')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

    </AppShell>
  );
}
